// ─────────────────────────────────────────────────────────────────────────────
// API client for the Zoidberg Cloudflare Worker.
// Offline-first: falls back to localStorage when the API is unreachable.
// ─────────────────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || "/";
const API_TOKEN = import.meta.env.VITE_API_TOKEN || "";

export type SyncEvent = {
  id?: number;
  date: string;
  label: string;
  start_min?: number;
  duration_min?: number;
  location?: string;
  is_fixed?: boolean;
  source?: "manual" | "calendar";
  external_id?: string;
  calendar_name?: string;
};

async function api(path: string, opts: RequestInit = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${API_TOKEN}`,
      ...opts.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ─── Config ─────────────────────────────────────────────────────────────────

export async function syncConfigToCloud(config: object): Promise<void> {
  try {
    await api("/api/config", { method: "POST", body: JSON.stringify(config) });
  } catch (e) {
    console.warn("Config sync failed (offline?), will retry later:", e);
    queueOfflineSync("config", config);
  }
}

export async function fetchConfigFromCloud(): Promise<object | null> {
  try {
    const { profile } = await api("/api/config");
    return profile;
  } catch {
    return null;
  }
}

// ─── Events ─────────────────────────────────────────────────────────────────

export async function fetchEvents(date: string): Promise<any[]> {
  try {
    // Add cache-buster to bypass service worker cache after deployments
    const cacheBuster = `&_t=${Date.now()}`;
    const { events } = await api(`/api/events?date=${encodeURIComponent(date)}${cacheBuster}`);
    return events;
  } catch {
    return loadOfflineEvents(date);
  }
}

export async function createEvent(event: object): Promise<{ id: number }> {
  const result = await api("/api/events", { method: "POST", body: JSON.stringify(event) });
  cacheOfflineEvent(event);
  return result;
}

export async function deleteEvent(id: number): Promise<void> {
  await api(`/api/events/${id}`, { method: "DELETE" });
  removeOfflineEvent(id);
}

// ─── Tasks ──────────────────────────────────────────────────────────────────

export async function fetchTasks(date: string): Promise<any[]> {
  try {
    const { tasks } = await api(`/api/tasks?date=${encodeURIComponent(date)}`);
    return tasks;
  } catch {
    return loadOfflineTasks(date);
  }
}

export async function createTask(task: object): Promise<{ id: number }> {
  const result = await api("/api/tasks", { method: "POST", body: JSON.stringify(task) });
  cacheOfflineTask(task);
  return result;
}

export async function toggleTask(id: number): Promise<void> {
  await api(`/api/tasks/${id}/toggle`, { method: "POST" });
}

export async function moveTask(id: number, toDate: string): Promise<void> {
  await api(`/api/tasks/${id}/move`, {
    method: "POST",
    body: JSON.stringify({ to_date: toDate }),
  });
}

export async function dropTask(id: number): Promise<void> {
  await api(`/api/tasks/${id}/drop`, { method: "POST" });
}

// ─── Escalation ─────────────────────────────────────────────────────────────

export async function fetchEscalation(): Promise<any | null> {
  try {
    const { state } = await api("/api/escalation");
    return state;
  } catch {
    return null;
  }
}

// ─── Call History ───────────────────────────────────────────────────────────

export async function fetchCallHistory(limit = 30): Promise<any[]> {
  try {
    const { calls } = await api(`/api/calls?limit=${limit}`);
    return calls;
  } catch {
    return [];
  }
}

// ─── Offline Cache Helpers ──────────────────────────────────────────────────

const OFFLINE_KEY = "zoidberg.offline";

function getOfflineCache() {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_KEY) || "{}");
  } catch {
    return {};
  }
}

function setOfflineCache(cache: any) {
  localStorage.setItem(OFFLINE_KEY, JSON.stringify(cache));
}

function cacheOfflineEvent(event: any) {
  const cache = getOfflineCache();
  const date = event.date;
  cache.events = cache.events || {};
  cache.events[date] = cache.events[date] || [];
  cache.events[date].push(event);
  setOfflineCache(cache);
}

function removeOfflineEvent(id: number) {
  const cache = getOfflineCache();
  if (!cache.events) return;
  for (const date of Object.keys(cache.events)) {
    cache.events[date] = cache.events[date].filter((e: any) => e.id !== id);
  }
  setOfflineCache(cache);
}

function loadOfflineEvents(date: string): any[] {
  const cache = getOfflineCache();
  return cache.events?.[date] || [];
}

function cacheOfflineTask(task: any) {
  const cache = getOfflineCache();
  const date = task.date;
  cache.tasks = cache.tasks || {};
  cache.tasks[date] = cache.tasks[date] || [];
  cache.tasks[date].push(task);
  setOfflineCache(cache);
}

function loadOfflineTasks(date: string): any[] {
  const cache = getOfflineCache();
  return cache.tasks?.[date] || [];
}

function queueOfflineSync(type: string, data: any) {
  const cache = getOfflineCache();
  cache.queue = cache.queue || [];
  cache.queue.push({ type, data, ts: Date.now() });
  setOfflineCache(cache);
}

// Attempt to flush queued offline actions when we come back online
export async function flushOfflineSync() {
  const cache = getOfflineCache();
  if (!cache.queue?.length) return;
  const queue = [...cache.queue];
  cache.queue = [];
  setOfflineCache(cache);

  for (const item of queue) {
    try {
      if (item.type === "config") {
        await syncConfigToCloud(item.data);
      }
    } catch {
      // Re-queue if still failing
      cache.queue = cache.queue || [];
      cache.queue.push(item);
      setOfflineCache(cache);
    }
  }
}

// Listen for online events to trigger sync
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    flushOfflineSync();
  });
}
